import { useEffect } from 'react';
import { NextPage } from 'next';
import triggerTracking from '@blueprint/tracking';
import Router, { useRouter } from 'next/router';
import { ApolloError } from '@apollo/client';
import { wrapper } from 'store';
import SearchResults, { SearchResultProps } from '../SearchResults';
import graphQLWrapper from '../../shared/graphql/withApollo';
import SearchResultsLayout from '../Layout';
import {
    LISTINGS_QUERY,
    LISTINGS_QUERY_V2
} from '../../queries/search/listings';
import { REDIRECT_QUERY } from '../../queries/search/redirects';
import { removeEverythingAfterHash } from '../../shared/helpers';
import { NextPageContextApp } from 'shared/types/search.types';
import useClientSideRoutingLoading from 'shared/hooks/useClientSideRoutingLoading';

import {
    SearchProvider,
    Redirect,
    ServerData,
    SearchContextType,
    AnalyticsTaxonomy,
    SeoBlurb,
    generateInitialSearchState,
    SearchType
} from '../SearchContext';
import { DfpTargeting } from '@zoopla/adverts';
import { PropertyOrPromoItem } from '../helpers/featuredProperties';
import { GraphQLError } from 'graphql';
import { Links } from '../SearchContext';
import { dispatchData } from 'shared/utils/search/dispatch-data';
import {
    getServerRenderedData,
    getServerRenderedDataV2
} from 'shared/utils/search/get-server-rendered-data';
import { Listing } from 'graphql.generated';
import { getOptimizelyFeatureFlagOnServer } from '@zoopla/optimizely';
import cookies from 'nookies';
import { getAnonId } from '@zoopla/tracking';
import snakeCase from 'lodash/snakeCase';

export type SearchPageProps = {
    searchResults?: ServerData['searchResults'];
    discoverMore?: ServerData['discoverMore'];
    breadcrumbs?: ServerData['breadcrumbs'];
    initialState: SearchContextType;
    filtersTargeting?: DfpTargeting;
    analyticsTaxonomy?: AnalyticsTaxonomy;
    regularListingsFormatted?: Listing[];
    extendedListingsFormatted?: Listing[];
    featuredProperties?: PropertyOrPromoItem[];
    transactionType?: SeoBlurb['transactionType'];
    canonicalPath?: string;
    error?: ApolloError;
    links?: Links;
    useNewQuery?: boolean;
};

const ANALYTICS_BASE_TAXONOMY = {
    country_code: 'gb',
    platform: 'React',
    brand_primary: 'zoopla',
    brand: 'zoopla'
};

const snakeCaseKeys = <T extends Record<string, unknown>>(obj: T) =>
    Object.entries(obj).reduce<T>(
        (acc, [key, value]) => ({
            ...acc,
            ...{ [snakeCase(key)]: value }
        }),
        {} as T
    );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleRedirects = (redirects: Redirect[], res: any) => {
    const { redirectUri, code } = redirects[0];

    // always a server redirect as we don't route to routes that need
    // redirects internally i.e. on the browser
    if (res) {
        res.writeHead(code, { Location: redirectUri });
        res.end();
    }
};

type SearchPageFactoryProps = {
    type: SearchType;
};

const isGeoGraphqlError = (err: GraphQLError): boolean => {
    const errors = err?.extensions?.response?.body?.errors;

    if (!Array.isArray(errors)) {
        return false;
    }

    const { validation_errors = [] } = errors[0];

    if (!Array.isArray(validation_errors)) {
        return false;
    }

    const { field } = validation_errors[0] || {};

    return field === 'geo_identifier';
};

const getGeoError = (error: ApolloError) => {
    const { graphQLErrors = [] } = error;

    return graphQLErrors.find((err) => isGeoGraphqlError(err));
};

const SearchPageFactory = (props: SearchPageFactoryProps) => {
    const SearchPage: NextPage<SearchPageProps> = ({
        analyticsTaxonomy,
        initialState,
        regularListingsFormatted,
        extendedListingsFormatted,
        featuredProperties,
        canonicalPath,
        filtersTargeting,
        transactionType,
        searchResults,
        breadcrumbs,
        discoverMore,
        links,
        error,
        useNewQuery
    }) => {
        const loading = useClientSideRoutingLoading();
        const router = useRouter();

        useEffect(() => {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'optimizely_feature',
                eventAction: 'show_listings_halo',
                eventLabel: useNewQuery ? 'variation_1' : 'original'
            });
        }, [useNewQuery]);

        const { query } = router;

        if (typeof window !== 'undefined' && analyticsTaxonomy) {
            const addTaxonomyEarlyEvent = {
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'populate',
                eventLabel: 'react_taxonomy',
                taxonomy: {
                    ...ANALYTICS_BASE_TAXONOMY,
                    ...snakeCaseKeys(analyticsTaxonomy)
                }
            };
            triggerTracking(addTaxonomyEarlyEvent);
        }

        const { dfpAdTargeting, seoAccordions } = initialState;

        const currentPage =
            typeof query.pn === 'string' ? parseInt(query.pn) : 1;

        const numberOfRegularListings =
            regularListingsFormatted && regularListingsFormatted.length
                ? regularListingsFormatted.length
                : 0;

        return (
            <SearchProvider initialState={initialState}>
                <SearchResultsLayout
                    dfpAdTargeting={{ ...dfpAdTargeting, ...filtersTargeting }}
                    seoAccordions={seoAccordions}
                    numberOfRegularListings={numberOfRegularListings}
                    pageNumber={currentPage}
                    links={links as Links}
                >
                    <SearchResults
                        // Todo: Remove unknown type casting. Entire React component tree type refactoring required
                        regularListings={
                            (regularListingsFormatted ||
                                []) as unknown as SearchResultProps['regularListings']
                        }
                        extendedListings={
                            (extendedListingsFormatted ||
                                []) as unknown as SearchResultProps['extendedListings']
                        }
                        featuredProperties={featuredProperties || []}
                        canonicalPath={canonicalPath || ''}
                        searchResults={
                            searchResults || ({} as ServerData['searchResults'])
                        }
                        breadcrumbs={
                            breadcrumbs || ([] as ServerData['breadcrumbs'])
                        }
                        discoverMore={
                            discoverMore || ({} as ServerData['discoverMore'])
                        }
                        transactionType={transactionType || 'sale'}
                        loading={loading}
                        error={error}
                        links={links as Links}
                    />
                </SearchResultsLayout>
            </SearchProvider>
        );
    };
    SearchPage.getInitialProps = wrapper.getInitialPageProps(
        (store) => async (ctx: NextPageContextApp) => {
            const { res, asPath, apolloClient, query, req } = ctx;
            const path = removeEverythingAfterHash(asPath);
            const searchType = props.type;
            const initialSearchState = generateInitialSearchState({
                searchType
            });

            const isServer = !!req;

            if (typeof window === 'undefined') {
                // eslint-disable-next-line @typescript-eslint/no-var-requires
                const newrelic = require('newrelic');
                newrelic.setTransactionName(`/${searchType}/`);
            }

            if (isServer) {
                res?.setHeader('Link', [
                    '<https://lid.zoocdn.com>; rel="preconnect"',
                    '<https://st.zoocdn.com/>; rel="preconnect"',
                    '<https://r.zoocdn.com/>; rel="preconnect"',
                    '<https://api.segment.io>; rel="preconnect"',
                    '<https://scripts.prod.zoopla.co.uk>; rel="preconnect"',
                    '<https://securepubads.g.doubleclick.net>; rel="preconnect"',
                    '<https://www.googletagmanager.com>; rel="preconnect"',
                    '<https://gdpr.privacymanager.io>; rel="preconnect"',
                    '<https://gdpr-wrapper.privacymanager.io>; rel="preconnect"'
                ]);

                const { data: redirectsData = { redirects: [] } } =
                    (await apolloClient?.query({
                        query: REDIRECT_QUERY,
                        variables: { path }
                    })) || {};

                const { redirects } = redirectsData;

                const shouldRedirect = redirects && redirects.length;
                if (shouldRedirect) {
                    handleRedirects(redirects, res);

                    return {
                        initialState: initialSearchState
                    };
                }
            }

            try {
                let userAgent;
                if (isServer) {
                    userAgent = req?.headers['user-agent'];
                } else {
                    userAgent = navigator.userAgent;
                }

                const isMobile = Boolean(
                    userAgent?.match(
                        /\bAndroid\b.*\bMobile\b|BlackBerry|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i
                    )
                );

                const allCookies = cookies.get({ req });
                const anonId = getAnonId({ req });

                const useNewQuery = isMobile
                    ? await getOptimizelyFeatureFlagOnServer({
                          featureKey: 'show_listing_halo',
                          cookies: allCookies,
                          query,
                          userId: anonId,
                          userAgent,
                          overrideAttributes: {
                              device_type: isMobile ? 'mobile' : 'desktop'
                          }
                      })
                    : false;

                const listingsQuery = useNewQuery
                    ? LISTINGS_QUERY_V2
                    : LISTINGS_QUERY;
                const { data } =
                    (await apolloClient?.query({
                        query: listingsQuery,
                        variables: { path: path }
                    })) || {};

                const { breadcrumbs, discoverMore } = data;

                let searchResults;
                let serverRenderedData;

                if (useNewQuery) {
                    searchResults = data.searchResultsV2;
                    serverRenderedData = getServerRenderedDataV2(
                        data,
                        initialSearchState
                    );
                } else {
                    searchResults = data.searchResults;
                    serverRenderedData = getServerRenderedData(
                        data,
                        initialSearchState
                    );
                }

                const {
                    regularListingsFormatted,
                    extendedListingsFormatted,
                    featuredProperties,
                    searchContextData,
                    canonicalPath,
                    analyticsTaxonomy,
                    transactionType,
                    filtersTargeting,
                    links,
                    sortOrder,
                    totalResults,
                    pageNumber,
                    totalResultsWasLimited,
                    polyenc,
                    filtersFields
                } = serverRenderedData;

                dispatchData(
                    store,
                    {
                        sortOrder,
                        totalResults,
                        pageNumber,
                        totalResultsWasLimited,
                        polyenc,
                        filtersFields
                    },
                    query
                );

                return {
                    analyticsTaxonomy: analyticsTaxonomy,
                    initialState: searchContextData || initialSearchState,
                    regularListingsFormatted: regularListingsFormatted,
                    extendedListingsFormatted: extendedListingsFormatted,
                    featuredProperties,
                    canonicalPath: canonicalPath,
                    transactionType,
                    searchResults,
                    breadcrumbs,
                    discoverMore,
                    filtersTargeting,
                    links,
                    useNewQuery
                };
            } catch (error) {
                const geoError = getGeoError(error as ApolloError);
                if (geoError) {
                    const errLocation: string =
                        geoError?.extensions?.response?.body?.errors[0]
                            ?.validation_errors[0]?.current_value;

                    const section = asPath?.includes('/for-sale')
                        ? 'for-sale'
                        : 'to-rent';

                    if (isServer) {
                        const redirect: Redirect = {
                            redirectUri: `/search/?section=${section}&q=${
                                errLocation || ''
                            }`,
                            code: 302
                        };
                        handleRedirects([redirect], res);
                    } else {
                        await Router.push({
                            pathname: '/search/',
                            query: { section, q: errLocation }
                        });
                    }
                }

                return {
                    initialState: initialSearchState,
                    error
                };
            }
        }
    );

    return {
        // SearchPage exported here just for testing
        SearchPage,
        SearchPageWithApollo: graphQLWrapper(SearchPage)
    };
};

export default SearchPageFactory;
